import { combineReducers, configureStore, createAction, type UnknownAction } from '@reduxjs/toolkit'
import { useDispatch, useSelector } from 'react-redux'

import { sessionSlice } from '@/features/auth/slices/sessionSlice'
import { demoSlice } from '@/features/demo/demoSlice'
import { activityFeedSlice } from '@/features/activity-feed/slices/activityFeedSlice'
import { myWorkSlice } from '@/features/my-work/slices/myWorkSlice'
import { phaseGatesSlice } from '@/features/phase-gates/slices/phaseGatesSlice'
import { adminPortfolioSlice } from '@/features/projects/slices/adminPortfolioSlice'
import { projectDetailSlice } from '@/features/projects/slices/projectDetailSlice'
import { projectsSlice } from '@/features/projects/slices/projectsSlice'
import { projectFilesSlice } from '@/features/project-files/slices/projectFilesSlice'
import { realtimeSlice } from '@/features/realtime/slices/realtimeSlice'
import { shellSlice } from '@/features/shell/shellSlice'
import { versionsSlice } from '@/features/versions/slices/versionsSlice'
import { usersSlice } from '@/features/users/usersSlice'
import { clientsSlice } from '@/features/clients/clientsSlice'
import { notificationsSlice } from '@/features/notifications/notificationsSlice'
import { orgSlice } from '@/features/org/orgSlice'
import { auditSlice } from '@/features/audit/auditSlice'
import { messagesSlice } from '@/features/messages/messagesSlice'
import { hoursSlice } from '@/features/hours/slices/hoursSlice'
import { rateCardsSlice } from '@/features/rates/slices/rateCardsSlice'
import { budgetsSlice } from '@/features/budgets/slices/budgetsSlice'
import { meteringSlice } from '@/features/metering/slices/meteringSlice'
import { analyticsSlice } from '@/features/analytics/slices/analyticsSlice'
// PX-W6.5 (TASK-AAB07DC3) — the configuration surfaces. Same append-only rule.
import { moduleCatalogueSlice } from '@/features/modules/moduleCatalogueSlice'
import { credentialsSlice } from '@/features/credentials/credentialsSlice'
import { llmConfigSlice } from '@/features/llmconfig/llmConfigSlice'
import { automationSlice } from '@/features/automation/automationSlice'
import { descriptorsSlice } from '@/features/descriptors/descriptorsSlice'
import { playbooksSlice } from '@/features/playbooks/playbooksSlice'
// PX-W7.1 (TASK-A30F4433) — the intakes surface.
import { intakesSlice } from '@/features/intakes/slices/intakesSlice'
// PX-W7.2 (TASK-60028F48) — the requirements register.
import { requirementsSlice } from '@/features/requirements/slices/requirementsSlice'
// PX-W7.4 (TASK-41CE1DE2) — the build board.
import { boardSlice } from '@/features/board/slices/boardSlice'
// PX-W7.5 (TASK-501F9B15) — the migration surface.
import { migrationSlice } from '@/features/migration/slices/migrationSlice'
// PX-W7.3 (TASK-9CDBB044) — the Blueprint's authoritative decision register.
import { decisionsSlice } from '@/features/decisions/slices/decisionsSlice'
import { agentsSlice } from '@/features/agents/slices/agentsSlice'
import { agentSettingsSlice } from '@/features/agentsettings/slices/agentSettingsSlice'
// CR-BUILD-BOARD-EPICS-2026-09-26 (TASK-41CE1DE2) — Generate tasks from the register.
import { ticketGenerationSlice } from '@/features/requirements/slices/ticketGenerationSlice'
// TASK-714FBFBD (CR-SSO-INVITE-PX) - configured SSO providers
import { oauthProvidersSlice } from '@/features/auth/slices/oauthProvidersSlice'
import { registerActiveOrgId } from '@/services'
import { listenerMiddleware } from '@/store/listenerMiddleware'
import '@/features/auth/slices/activeOrgListener' // TASK-7F3EE34E — persists + restores the active org
import '@/store/cacheFollowUps' // TASK-3D6DB8B6 — registers the cache follow-up listener
import '@/features/projects/projectFrameRefetch' // TASK-7922504E fix cycle 1 — id-only project frames refetch via REST

/**
 * Redux Toolkit store — the workspace's standing store choice for prototypes
 * and demos (mirrors projects/xelerate-ai/application/client/src/store/).
 *
 * W6.1 adds the two substrate slices beside the prototype pair:
 *  - `session`  — the REAL session (status, user, derived roleKey, activeOrg)
 *  - `realtime` — the websocket skeleton's nonces / shells / echo counter
 *
 * `resetAll` is a ROOT action handled by a wrapper around the combined
 * reducer (not a per-slice `reset` case): dispatching it returns every slice
 * to its initial state. Wired to a visible control in the app shell.
 * Nothing is persisted — a reload is also a reset.
 */
export const resetAll = createAction('root/resetAll')

/** Exported so the test harness builds its stores from THIS reducer, never a copy. */
export const combinedReducer = combineReducers({
  shell: shellSlice.reducer,
  demo: demoSlice.reducer,
  session: sessionSlice.reducer,
  realtime: realtimeSlice.reducer,
  // PX-W6.3 (TASK-EDD04F2F) — the projects/engagement surfaces. APPEND-ONLY
  // keys beside the substrate pair; W6.2-W6.5 add theirs the same way.
  projects: projectsSlice.reducer,
  // PX-W7.9 (TASK-17A2C062) — the admin "Projects & people" landing's
  // roster-free portfolio read. Same append-only rule.
  adminPortfolio: adminPortfolioSlice.reducer,
  projectDetail: projectDetailSlice.reducer,
  phaseGates: phaseGatesSlice.reducer,
  projectVersions: versionsSlice.reducer,
  projectFiles: projectFilesSlice.reducer,
  activityFeed: activityFeedSlice.reducer,
  myWork: myWorkSlice.reducer,
  // PX-W6.2 (TASK-A7B7080E) — the org/people/clients surfaces. Same
  // append-only rule.
  users: usersSlice.reducer,
  clients: clientsSlice.reducer,
  notifications: notificationsSlice.reducer,
  org: orgSlice.reducer,
  audit: auditSlice.reducer,
  messages: messagesSlice.reducer,
  // PX-W6.4 (TASK-191D0FE9) — the commercial spine: hours, rate cards,
  // budgets, run-budget metering, project analytics. Same append-only rule.
  hours: hoursSlice.reducer,
  rateCards: rateCardsSlice.reducer,
  budgets: budgetsSlice.reducer,
  metering: meteringSlice.reducer,
  analytics: analyticsSlice.reducer,
  // PX-W6.5 (TASK-AAB07DC3) — the configuration surfaces: module catalogue,
  // outbound credentials + OAuth, per-project LLM config, scheduler +
  // context index, agent descriptors, playbooks + tenant policy.
  modules: moduleCatalogueSlice.reducer,
  credentials: credentialsSlice.reducer,
  llm: llmConfigSlice.reducer,
  automation: automationSlice.reducer,
  descriptors: descriptorsSlice.reducer,
  playbooks: playbooksSlice.reducer,
  // PX-W7.1 (TASK-A30F4433) — the intakes surface.
  intakes: intakesSlice.reducer,
  // PX-W7.2 (TASK-60028F48) — the requirements register.
  requirements: requirementsSlice.reducer,
  // PX-W7.4 (TASK-41CE1DE2) — the build board.
  board: boardSlice.reducer,
  // PX-W7.5 (TASK-501F9B15) — the migration surface.
  migration: migrationSlice.reducer,
  // PX-W7.3 (TASK-9CDBB044) — open decisions on the Blueprint hybrid.
  decisions: decisionsSlice.reducer,
  // PX-W7.7 (TASK-4EE55E6C) — organisation agents plus the canonical FIRM
  // draft/version settings boundary that W7.8 extends.
  agents: agentsSlice.reducer,
  agentSettings: agentSettingsSlice.reducer,
  // CR-BUILD-BOARD-EPICS-2026-09-26 (TASK-41CE1DE2) — Generate tasks from the register.
  ticketGeneration: ticketGenerationSlice.reducer,
  // TASK-714FBFBD (CR-SSO-INVITE-PX) - configured SSO providers
  oauthProviders: oauthProvidersSlice.reducer
})

export const rootReducer = (
  state: ReturnType<typeof combinedReducer> | undefined,
  action: UnknownAction
) => {
  if (resetAll.match(action)) {
    const fresh = combinedReducer(undefined, action)
    /*
     * The ONE deliberate exception to "reset returns every slice to its
     * initial state" (AC-8.8, amended I-8): clearing the concept acts is what
     * the control means; ending the session is what SIGN OUT means. A demo
     * affordance must never sign a real user out.
     *
     * PX-W6.6 removed the second half of this carve-out with the demo lens
     * itself: the demo slice no longer holds a session, so `fresh.demo` is
     * simply the empty act log. Do not "fix" the session line back to an
     * unconditional reset.
     */
    const session = state?.session
    return session ? { ...fresh, session } : fresh
  }
  return combinedReducer(state, action)
}

export const store = configureStore({
  reducer: rootReducer,
  middleware: (getDefaultMiddleware) => getDefaultMiddleware().concat(listenerMiddleware.middleware),
  devTools: import.meta.env.DEV
})

/*
 * Decision D7: `{orgId}` URL placeholders on the shared transport read the
 * session slice's active org (registered here, where that state lives — the
 * service layer stays free of a store import, so no import cycle).
 */
registerActiveOrgId(() => store.getState().session.activeOrgId)

// RootState is derived from the REDUCER, not the store instance: the store's
// type includes the listener middleware, whose own pre-typing imports
// RootState — deriving from the instance would be a type-level cycle
// (TS2456). The RTK-documented shape for pre-typed listener middleware.
export type RootState = ReturnType<typeof rootReducer>

// AppDispatch is likewise derived WITHOUT the real store instance, from a
// TYPE-ONLY minimal configureStore instantiation (never called): the listener
// middleware's type mentions AppDispatch, so `typeof store.dispatch` would
// complete the same cycle. The listener middleware is dispatch-transparent,
// so this dispatch type equals the real store's.
type MinimalStore = ReturnType<typeof configureStore<{ reducer: typeof rootReducer }>>
export type AppDispatch = MinimalStore['dispatch']

export const useAppDispatch = useDispatch.withTypes<AppDispatch>()
export const useAppSelector = useSelector.withTypes<RootState>()
